import type { RawVisualGrouping } from "../visualGroups";
import { defaultThemeV2 } from "../../theme/defaultThemeV2";
import type { QuestionCore } from "../contracts";

export type TextRaw = RawVisualGrouping & {
  type: "text";
  content: string;
  previous_response?: string;
  image?: string;
  alt_text?: string | null;
  error_message?: string;
  dont_know_option?: { value: string; text: string } | null;
};

export type TextDefinition = {
  prompt: string;
  previousResponse: string;
  image?: string;
  altText?: string;
  error?: string;
  dontKnow?: { value: string; label: string };
};

export type TextDraft = { value: string; dontKnowSelected: boolean };
export type TextEvent =
  | { type: "set-value"; value: string }
  | { type: "toggle-dont-know" };
export type TextResponse = { type: "text"; value: string };

export const textCore: QuestionCore<
  TextRaw,
  TextDefinition,
  TextDraft,
  TextEvent,
  TextResponse
> = {
  decode: (raw) => {
    if (
      raw.previous_response !== undefined &&
      typeof raw.previous_response !== "string"
    ) {
      throw new Error("Text previous response must be a string.");
    }
    return {
      prompt: raw.content,
      previousResponse: raw.previous_response ?? "",
      ...(raw.image ? { image: raw.image } : {}),
      ...(raw.alt_text ? { altText: raw.alt_text } : {}),
      ...(raw.error_message ? { error: raw.error_message } : {}),
      ...(raw.dont_know_option
        ? {
            dontKnow: {
              value: raw.dont_know_option.value,
              label: raw.dont_know_option.text,
            },
          }
        : {}),
    };
  },
  initialize: (definition) => {
    const dontKnowSelected =
      definition.dontKnow?.value === definition.previousResponse;
    return {
      value: dontKnowSelected ? "" : definition.previousResponse,
      dontKnowSelected,
    };
  },
  update: (_definition, draft, event) =>
    event.type === "toggle-dont-know"
      ? { value: "", dontKnowSelected: !draft.dontKnowSelected }
      : { value: event.value, dontKnowSelected: false },
  validate: (definition, draft, copy = defaultThemeV2.copy) =>
    (draft.dontKnowSelected && Boolean(definition.dontKnow)) ||
    draft.value.length > 0
      ? { valid: true }
      : {
          valid: false,
          error: copy.validation.response,
          fieldId: "text-answer",
        },
  encode: (definition, draft) => {
    const validation = textCore.validate(definition, draft);
    if (!validation.valid) throw new Error(validation.error);
    return {
      type: "text",
      value:
        draft.dontKnowSelected && definition.dontKnow
          ? definition.dontKnow.value
          : draft.value,
    };
  },
  projectTranscript: (definition, draft) => ({
    kind: "text",
    question: definition.prompt,
    answer:
      draft.dontKnowSelected && definition.dontKnow
        ? definition.dontKnow.label
        : draft.value,
  }),
};
